import { useRef, useState, useCallback, useEffect, useMemo } from "react";
import * as THREE from "three";
import { useThree, useFrame, ThreeEvent } from "@react-three/fiber";
import { useGLTF, Text } from "@react-three/drei";
import { Howl } from "howler";

// 지퍼 이빨 간격 (고정)
const ZIPPER_TOOTH_SPACING = 0.08;

interface ZipperToothProps {
  index: number;
  side: "left" | "right";
  startY: number;
  toothSpacing: number;
  zipperY: number;
  maxSeparation?: number;
  totalLength: number;
}

function ZipperTooth({
  index,
  side,
  startY,
  toothSpacing,
  zipperY,
  maxSeparation = 0.15,
  totalLength,
}: ZipperToothProps) {
  const sideOffset = side === "right" ? toothSpacing * 0.5 : 0;
  const toothY = startY - index * toothSpacing - sideOffset;
  const isBelowZipper = toothY < zipperY;

  let separation = 0;
  if (isBelowZipper) {
    const distanceFromZipper = zipperY - toothY;
    const normalizedDistance = Math.min(
      1,
      distanceFromZipper / (totalLength * 0.3)
    );
    separation = maxSeparation * (1 - Math.pow(1 - normalizedDistance, 2));
  }

  const baseX = side === "left" ? -0.04 : 0.04;
  const separatedX = side === "left" ? baseX - separation : baseX + separation;
  const color = isBelowZipper ? "#555555" : "#777777";

  return (
    <mesh position={[separatedX, toothY, 0]}>
      <boxGeometry args={[0.06, 0.03, 0.02]} />
      <meshStandardMaterial color={color} metalness={0.6} roughness={0.4} />
    </mesh>
  );
}

/**
 * 지퍼 인디케이터 (화살표)
 */
function ZipperIndicator({
  position,
  rotation,
}: {
  position: [number, number, number];
  rotation: [number, number, number];
}) {
  return (
    <group position={position} rotation={rotation}>
      {/* 화살표 몸통 */}
      <mesh position={[0, -0.05, 0]}>
        <boxGeometry args={[0.04, 0.12, 0.02]} />
        <meshStandardMaterial color="#aaaaaa" emissive="#555555" />
      </mesh>
      {/* 화살표 머리 */}
      <mesh position={[0, 0.04, 0]}>
        <coneGeometry args={[0.1, 0.15, 3]} />
        <meshStandardMaterial color="#aaaaaa" emissive="#555555" />
      </mesh>
    </group>
  );
}

interface BottomRightZipperSliderProps {
  /** 현재 progress 값 (0~1) */
  progress?: number;
  /** progress 변경 콜백 */
  onProgressChange?: (value: number) => void;
  /** 시작점 텍스트 (progress=0 근처에 표시) */
  startText?: string;
  /** 끝점 텍스트 (progress=1 근처에 표시) */
  endText?: string;
  /** 전체 씬 위치 */
  position?: [number, number, number];
  /** 텍스트 색상 */
  textColor?: string;
  /** 지퍼 길이 */
  zipperLength?: number;
  /** 활성화 여부 */
  enabled?: boolean;
  scale?: number;
  rotation?: [number, number, number];
}

/**
 * BottomRight 전용 지퍼 슬라이더 컴포넌트
 * 텍스트가 지퍼 progress 위치에 따라 배치됨
 */
export function BottomRightZipperSlider({
  progress: externalProgress,
  onProgressChange,
  startText = "입주하기",
  endText = "ZIP 닫기",
  position = [0, 0, 0],
  textColor = "#ffffff",
  rotation = [0, 0, 0],
  zipperLength = 3,
  enabled = true,
  scale = 1,
}: BottomRightZipperSliderProps) {
  const { gl, camera } = useThree();

  // 내부 progress 상태
  const [internalProgress, setInternalProgress] = useState(0.5);
  const progress = externalProgress ?? internalProgress;
  const setProgress = onProgressChange ?? setInternalProgress;

  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const startMouseRef = useRef<{ x: number; y: number } | null>(null);
  const startValueRef = useRef<number>(0);

  // 드래그 속도 추적용
  const lastMouseYRef = useRef<number>(0);
  const lastTimeRef = useRef<number>(0);
  const dragVelocityRef = useRef<number>(0);

  // Howler 인스턴스 (지퍼 사운드)
  const zipperSoundRef = useRef<Howl | null>(null);

  // Howl 인스턴스 초기화
  useEffect(() => {
    zipperSoundRef.current = new Howl({
      src: [import.meta.env.BASE_URL + "/sound/zipper.mp3"],
      volume: 0.3,
      loop: true,
      sprite: {
        short: [100, 300], // 0~800ms 구간만 사용
      },
    });

    return () => {
      zipperSoundRef.current?.unload();
    };
  }, []);

  // 드래그 상태 변경 시 사운드 제어
  useEffect(() => {
    const sound = zipperSoundRef.current;
    if (!sound) return;

    if (isDragging) {
      // 드래그 시작: 사운드 재생
      sound.play("short");
    } else {
      // 드래그 종료: 사운드 정지
      sound.stop();
    }
  }, [isDragging]);

  // Zipper 모델 로드
  const { scene } = useGLTF(import.meta.env.BASE_URL + "/models/zipper.glb");

  // 모델 클론
  const clonedScene = useMemo(() => {
    if (!scene) return null;
    const clone = scene.clone();
    clone.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.material = new THREE.MeshStandardMaterial({
          color: "#888888",
          metalness: 0.7,
          roughness: 0.3,
        });
      }
    });
    return clone;
  }, [scene]);

  // 호버/드래그 상태에 따른 머티리얼 업데이트
  useFrame(() => {
    if (clonedScene) {
      clonedScene.traverse((child) => {
        if (
          child instanceof THREE.Mesh &&
          child.material instanceof THREE.MeshStandardMaterial
        ) {
          child.material.color.set(isDragging ? "#666666" : "#999999");
          child.material.emissiveIntensity = isDragging ? 0.2 : 0.1;
        }
      });
    }
  });

  // 지퍼 시작 Y 위치
  const zipperStartY = 0.3;

  // 화면 픽셀을 월드 단위로 변환
  const getPixelToWorldRatio = useCallback(() => {
    const orthoCamera = camera as THREE.OrthographicCamera;
    const viewHeight =
      (orthoCamera.top - orthoCamera.bottom) / orthoCamera.zoom;
    const screenHeight = gl.domElement.clientHeight;
    return viewHeight / screenHeight;
  }, [camera, gl]);

  // 드래그 핸들러
  const handlePointerDown = useCallback(
    (e: ThreeEvent<PointerEvent>) => {
      if (!enabled) return;
      e.stopPropagation();
      setIsDragging(true);
      startMouseRef.current = { x: e.clientX, y: e.clientY };
      startValueRef.current = progress;
      lastMouseYRef.current = e.clientY;
      lastTimeRef.current = performance.now();
      gl.domElement.setPointerCapture(e.pointerId);
    },
    [enabled, progress, gl]
  );

  const handlePointerMove = useCallback(
    (e: PointerEvent) => {
      if (!isDragging || !startMouseRef.current || !enabled) return;

      const now = performance.now();
      const deltaTime = now - lastTimeRef.current;
      const deltaY = Math.abs(e.clientY - lastMouseYRef.current);

      // 속도 계산 (픽셀/ms)
      if (deltaTime > 0) {
        const velocity = deltaY / deltaTime;
        // 부드러운 보간
        dragVelocityRef.current =
          dragVelocityRef.current * 0.7 + velocity * 0.3;

        // 재생 속도 조절 (최소 0.5x, 최대 1x)
        const playbackRate = Math.max(
          0.3,
          Math.min(1, 0.5 + dragVelocityRef.current * 2)
        );

        // Howler로 재생 속도 및 볼륨 변경
        if (zipperSoundRef.current) {
          // 데드존: 속도가 0.05 이하면 볼륨 0 (소리 안 들림)
          const DEAD_ZONE_THRESHOLD = 0.05;
          if (dragVelocityRef.current < DEAD_ZONE_THRESHOLD) {
            zipperSoundRef.current.volume(0);
          } else {
            zipperSoundRef.current.volume(0.3);
            zipperSoundRef.current.rate(playbackRate);
          }
        }
      }

      lastMouseYRef.current = e.clientY;
      lastTimeRef.current = now;

      // 기존 progress 업데이트 로직
      const deltaScreenY = e.clientY - startMouseRef.current.y;
      const pixelToWorld = getPixelToWorldRatio();
      const deltaWorldY = deltaScreenY * pixelToWorld;
      const progressDelta = deltaWorldY / zipperLength;
      const newValue = Math.max(
        0,
        Math.min(1, startValueRef.current + progressDelta)
      );
      setProgress(newValue);
    },
    [isDragging, enabled, setProgress, getPixelToWorldRatio, zipperLength]
  );

  const handlePointerUp = useCallback(
    (e: PointerEvent) => {
      if (!isDragging) return;
      setIsDragging(false);
      startMouseRef.current = null;
      dragVelocityRef.current = 0;
      gl.domElement.releasePointerCapture(e.pointerId);
    },
    [isDragging, gl]
  );

  useEffect(() => {
    if (!enabled) return;
    document.addEventListener("pointermove", handlePointerMove);
    document.addEventListener("pointerup", handlePointerUp);
    return () => {
      document.removeEventListener("pointermove", handlePointerMove);
      document.removeEventListener("pointerup", handlePointerUp);
    };
  }, [enabled, handlePointerMove, handlePointerUp]);

  // 지퍼 위치 계산
  const zipperY = zipperStartY - progress * zipperLength;

  // 지퍼 이빨 데이터
  const zipperTeethData = useMemo(() => {
    const teethCount = Math.max(
      2,
      Math.floor(zipperLength / ZIPPER_TOOTH_SPACING) + 1
    );
    const teeth: Array<{ index: number; side: "left" | "right" }> = [];
    for (let i = 0; i < teethCount; i++) {
      teeth.push({ index: i, side: "left" });
      teeth.push({ index: i, side: "right" });
    }
    return { teeth, teethCount };
  }, [zipperLength]);

  // 텍스트 위치 계산 (progress 위치에 따라)
  // 시작 텍스트: progress=0일 때 지퍼 시작 위치
  const startTextY = zipperStartY;
  // 끝 텍스트: progress=1일 때 지퍼 끝 위치
  const endTextY = zipperStartY - zipperLength;

  // 텍스트 투명도 (progress에 따라)
  // 시작 텍스트: progress가 0에 가까울수록 보임
  const startTextOpacity = Math.max(0, 1 - progress * 2);
  // 끝 텍스트: progress가 1에 가까울수록 보임
  const endTextOpacity = Math.max(0, (progress - 0.5) * 2);

  return (
    <group position={position} rotation={rotation} scale={scale}>
      {/* 지퍼 배경 */}
      <mesh position={[0, zipperStartY - zipperLength / 2, -0.01]}>
        <planeGeometry args={[0.5, zipperLength]} />
        <meshStandardMaterial color="#2a2a2a" side={THREE.DoubleSide} />
      </mesh>

      {/* 지퍼 이빨들 */}
      {zipperTeethData.teeth.map(({ index, side }) => (
        <ZipperTooth
          key={`tooth-${side}-${index}`}
          index={index}
          side={side}
          startY={zipperStartY}
          toothSpacing={ZIPPER_TOOTH_SPACING}
          zipperY={zipperY}
          maxSeparation={0.2}
          totalLength={zipperLength}
        />
      ))}

      {/* 지퍼 중앙 라인 */}
      <mesh position={[0, (zipperStartY + zipperY) / 2, 0.005]}>
        <boxGeometry
          args={[0.02, Math.max(0.01, zipperStartY - zipperY), 0.01]}
        />
        <meshStandardMaterial color="#444444" metalness={0.8} roughness={0.2} />
      </mesh>

      {/* Zipper 슬라이더 */}
      <group
        position={[0, zipperY, 0.05]}
        scale={0.12}
        rotation={[Math.PI / 2, 0, 0]}
        onPointerDown={handlePointerDown}
        onPointerEnter={() => setIsHovered(true)}
        onPointerLeave={() => !isDragging && setIsHovered(false)}
      >
        {clonedScene ? (
          <primitive object={clonedScene} />
        ) : (
          <group>
            <mesh>
              <boxGeometry args={[1.5, 0.6, 0.4]} />
              <meshStandardMaterial
                color={
                  isDragging ? "#ffffff" : isHovered ? "#aaaaaa" : "#666666"
                }
                metalness={0.8}
                roughness={0.2}
              />
            </mesh>
          </group>
        )}
      </group>

      {/* 시작점 인디케이터 (위쪽 화살표) */}
      <ZipperIndicator position={[0, zipperStartY + 0.3, 0]} rotation={[0, 0, 0]} />

      {/* 끝점 인디케이터 (아래쪽 화살표) */}
      <ZipperIndicator
        position={[0, endTextY - 0.3, 0]}
        rotation={[0, 0, Math.PI]}
      />

      {/* 시작점 텍스트 (progress=0 근처 - 90도 회전, 왼쪽으로 이동, 180도 추가 회전) */}
      <Text
        position={[-0.8, startTextY, 0]}
        fontSize={0.25}
        color={textColor}
        anchorX="center"
        anchorY="middle"
        rotation={[0, 0, Math.PI / 2]}
        fillOpacity={startTextOpacity}
        outlineWidth={0.015}
        outlineColor="#000000"
        outlineOpacity={startTextOpacity * 0.5}
      >
        {startText}
      </Text>

      {/* 끝점 텍스트 (progress=1 근처 - 90도 회전, 왼쪽으로 이동, 180도 추가 회전) */}
      <Text
        position={[-0.8, endTextY, 0]}
        fontSize={0.25}
        color={textColor}
        anchorX="center"
        anchorY="middle"
        rotation={[0, 0, Math.PI / 2]}
        fillOpacity={endTextOpacity}
        outlineWidth={0.015}
        outlineColor="#000000"
        outlineOpacity={endTextOpacity * 0.5}
      >
        {endText}
      </Text>
    </group>
  );
}

// GLB 파일 프리로드
useGLTF.preload("/models/zipper.glb");
